<template>
    <div class="table-container w-full">
        <div class="mt-1 flex justify-between lg:flex-row flex-col">
            <div class="lg:w-1/3 w-full">
                <a-input-search
                    data-cy="deleted-logs-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div class="lg:w-1/2 xl:w-2/5 w-full" v-if="showFilter" data-cy="deleted-logs-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    :filter-option="false"
                    :not-found-content="null"
                    @search="handleFilterSearch"
                    @change="handleFilterChange"
                    :allowClear="true"
                >
                    <a-select-opt-group
                        v-if="
                            company_list.some(
                                (value) =>
                                    value.association_code.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Company"
                    >
                        <template v-for="(value, index) in company_list">
                            <a-select-option
                                v-if="
                                    value.association_code.toLowerCase().includes(filter_value.toLowerCase()) ||
                                    value.company_name.toLowerCase().includes(filter_value.toLowerCase())
                                "
                                :key="`${index}-company`"
                                :value="`${index}-company`"
                            >
                                {{ value.association_code }} - {{ value.company_name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                    <a-select-opt-group
                        v-if="
                            association_list.some((value) =>
                                value.name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Association"
                    >
                        <template v-for="(value, index) in association_list">
                            <a-select-option
                                v-if="value.name.toLowerCase().includes(filter_value.toLowerCase())"
                                :key="`${index}-association`"
                                :value="`${index}-association`"
                            >
                                {{ value.name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                </a-select>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="deleted-log-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'claim_id'">
                            <span class="whitespace-nowrap">
                                {{ text }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'insured_name'">
                            <span class="whitespace-nowrap">
                                {{ textTruncate(text) }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'date_of_loss'">
                            <span class="whitespace-nowrap">
                                {{ text }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'created_at'">
                            <span class="whitespace-nowrap">
                                {{ dateFormat(text) }}
                            </span>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            data: [],
            search_value: null,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            filter_value: '',
            company_list: [],
            association_list: [],
        };
    },
    props: {
        company_id: String,
        is_admin_section: Boolean,
    },
    mounted() {
        this.fetch();
        if (this.fetch_all) {
            this.$http
                .get('/api/admin/list-companies-by-associations')
                .then((response) => {
                    this.company_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCAL01' }),
                    });
                });
            this.$http
                .get('/api/admin/list-associations')
                .then((response) => {
                    this.association_list = response.data.data;
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    },
    computed: {
        getColumns() {
            return [
                {
                    title: this.$t('globals.tables.deleted_claim_history.headers.claim_id'),
                    key: 'claim_id',
                    dataIndex: 'claim_id',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.deleted_claim_history.headers.company_code'),
                    key: 'company_code',
                    dataIndex: 'company_code',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.deleted_claim_history.headers.date_of_loss'),
                    key: 'date_of_loss',
                    dataIndex: 'date_of_loss',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.deleted_claim_history.headers.file_source'),
                    key: 'file_source',
                    dataIndex: 'file_source',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.deleted_claim_history.headers.insured_name'),
                    key: 'insured_name',
                    dataIndex: 'insured_name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.deleted_claim_history.headers.last_ip_address'),
                    key: 'last_ip_address',
                    dataIndex: 'last_ip_address',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.deleted_claim_history.headers.deleted_date'),
                    key: 'created_at',
                    dataIndex: 'created_at',
                    sorter: true,
                },
            ];
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        showFilter() {
            if (this.is_admin_section) {
                this.fetch_all = true;
                return true;
            } else {
                return false;
            }
        },
    },
    methods: {
        fetch(params = {}) {
            this.loading = true;
            this.params = params;

            this.$http
                .get('/api/deleted-audit-logs', {
                    params: {
                        company_id: this.company_id,
                        fetch_all: this.fetch_all,
                        ...params,
                    },
                })
                .then((response) => {
                    const pagination = { ...this.pagination };

                    // Read total count from server
                    pagination.total = response.data.meta.total_count;

                    this.loading = false;
                    this.data = response.data.data;
                    this.pagination = pagination;
                    this.selected_row_keys = [];
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVDLF01' }),
                        });
                    }
                });
        },
        textTruncate(string) {
            if (string.length > 25) {
                return string.substring(0, 25) + '...';
            }
            return string;
        },
        dateFormat(date) {
            return this.$dayjs(date).format('YYYY-MM-DD');
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.fetch({
                    search_value: this.search_value,
                    company_id: this.params.company_id,
                    association_id: this.params.association_id,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        handleFilterSearch(value) {
            this.filter_value = value;
        },
        handleFilterChange(value) {
            let company_id = null;
            let association_id = null;
            let fetch_all = null;

            if (value == undefined) {
                fetch_all = true;
            } else {
                if (value.includes('company')) {
                    company_id = this.company_list[parseInt(value)].company_id;
                    association_id = this.company_list[parseInt(value)].association_id;
                } else {
                    association_id = this.association_list[parseInt(value)].id;
                }
            }

            this.fetch({
                fetch_all: fetch_all,
                search_value: this.search_value,
                company_id: company_id,
                association_id: association_id,
            });
            this.filter_value = '';
            this.pagination.current = 1;
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
    },
};
</script>
